import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';

import { getRuntimeUniqueId } from '@shared/utils/runtime-unique-id';

import { appCheck16Icon } from '@icons/check-16';
import { appIndeterminate16Icon } from '@icons/indeterminate-16';

@Component({
  selector: 'app-checkbox',
  imports: [SvgIconComponent, NgClass],
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
    },
  ],
  templateUrl: './checkbox.component.html',
  host: {
    class: 'flex flex-col items-start gap-1',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxComponent implements ControlValueAccessor {
  public id = input<string>();
  public label = input<string>();
  public indeterminate = input<boolean>();
  public size = input<'16' | '24'>('16');
  public readonly = input<boolean>(false);

  public readonly uuid = `${getRuntimeUniqueId()}`;
  public readonly disabled = signal(false);

  public checked = signal(false);

  private onChange: (checked: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    injectRegisterIcons([appCheck16Icon, appIndeterminate16Icon]);
  }

  public onCheckboxChange(event: Event): void {
    if (this.readonly() || this.disabled()) {
      return;
    }
    const target = event.target as HTMLInputElement;
    this.checked.set(target.checked);
    this.onChange(target.checked);
  }

  public onBlur(): void {
    this.onTouched();
  }

  public registerOnChange(onChange: (checked: boolean) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public writeValue(checked: boolean): void {
    this.checked.set(checked);
  }
}
